import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auth, intelligence, programs } from '@cwos/api-client';
import { t } from '@cwos/ui-shared';
import { BrandMark, Icon, SkeletonSet, useFocusTrap } from '@cwos/ui-admin';
import { firstNameOf, greetingKeyFor, type Suggestion } from '../suggestions.js';
import { ConversationSurface } from './ConversationSurface.js';
import { useThreadsClient } from '../history/threadsClient.js';
import { deriveScreenSuggestions } from './screenSuggestions.js';
import { screenContextNote, toWireContext, type ScreenContext } from './screenContext.js';

/**
 * Ask Phibian as a side sheet, over whatever screen the person is on.
 *
 * THE PANEL'S THREE PROMISES.
 *
 * 1. IT IS THE SAME ASSISTANT. Same route, same session semantics, same
 *    evidence and grounding rendering, same confirmation ceremony — the
 *    conversation is `ConversationSurface`, the ONE implementation the Ask
 *    Phibian page also renders (F224). The panel is chrome around it, not a
 *    second product, and a change to how an answer reads lands in both places
 *    or in neither.
 *
 * 2. IT CARRIES THE SCREEN, AND ONLY THE SCREEN. The typed context is built by
 *    `toWireContext` from a closed union of kinds and identifiers. Nothing is
 *    read off the page. And the panel SAYS what it is carrying, in words, by
 *    kind — "Questions here can be about the worker on this screen" — because
 *    a surface that quietly tells a model where you are should be a surface
 *    that tells you it is doing that. The note names a kind and never a
 *    person: naming the worker would mean reading a name the assistant has not
 *    been given.
 *
 * 3. WHAT IT OFFERS, THE SERVER OPENED. The starters come from
 *    `deriveScreenSuggestions` — the page's own derivation with the screen
 *    added — so they are gated on the tool list the SERVER resolved for this
 *    caller. Being able to see a worker profile is not authority to ask
 *    organization-shaped questions about the worker on it.
 *
 * MODALITY. The sheet is `aria-modal`, traps Tab, closes on Escape and on the
 * scrim, and hands focus back to the trigger on close (`useFocusTrap` does all
 * four). Focus lands on the composer, because the panel exists to be typed in.
 */

export interface AskPhibianPanelProps {
  /** The typed screen this panel was opened from, if this app describes it. */
  readonly screen: ScreenContext | undefined;
  readonly onClose: () => void;
  /** The clock behind the greeting, injected so a test can pin "Good evening". */
  readonly now?: Date | undefined;
  /** A question the composer opens with (a chart's "Ask why"); the person sends it. */
  readonly initialDraft?: string | undefined;
}

export function AskPhibianPanel({ screen, onClose, now, initialDraft }: AskPhibianPanelProps) {
  const composer = useRef<HTMLTextAreaElement | null>(null);
  const threadsClient = useThreadsClient();
  const panel = useFocusTrap<HTMLElement>(true, {
    onEscape: onClose,
    initialFocus: composer,
    contain: true,
  });

  const wireContext = toWireContext(screen);
  const note = screenContextNote(screen);

  return (
    <>
      {/* The scrim is a click target, not a control. `role="presentation"` is
          the literal claim — it is not in the accessibility tree, it takes no
          focus, and it has no keyboard path of its own BECAUSE Escape and the
          Close button already are the keyboard paths. A third tab stop that
          announces nothing would be noise, not access. (This is also the one
          exemption `accessibility.test.ts` grants a click-bearing div, and the
          exemption is only honest when the sentence above is true.) */}
      <div className="apg-scrim" role="presentation" onClick={onClose} aria-hidden="true" />
      <aside
        className="apg-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Ask Phibian"
        ref={panel}
      >
        <header className="apg-head">
          <h2 className="apg-title">
            <BrandMark size={18} />
            Ask Phibian
          </h2>
          <button type="button" className="rn-btn-quiet apg-close" onClick={onClose}>
            Close
          </button>
        </header>
        {note !== undefined ? (
          <p className="rn-muted apg-note">
            <Icon name="look" size={13} className="apg-note-icon" />
            {note} It still reads with your permissions, never the screen’s.
          </p>
        ) : null}
        <ConversationSurface
          wireContext={wireContext}
          composerId="intelligence-panel-question"
          composerRef={composer}
          initialDraft={initialDraft}
          /*
           * PROMISE 1 EXTENDS TO PERSISTENCE. A conversation started in the
           * panel is a conversation, and it is saved and listed exactly like
           * one started on the page — the panel has no sidebar to show it in,
           * which is a fact about this chrome and not a reason for the answer
           * to be forgotten. Without this, asking here and asking there would
           * have different consequences with nothing on screen saying so.
           */
          ensureThread={
            threadsClient === undefined
              ? undefined
              : async (firstMessage) => (await threadsClient.create({ firstMessage })).threadId
          }
          renderEmpty={(ask, disabled) => (
            <PanelEmptyState screen={screen} onPick={ask} disabled={disabled} now={now} />
          )}
        />
      </aside>
    </>
  );
}

/**
 * Before the first message: who is asking, and what this screen makes worth
 * asking.
 *
 * The three states of the suggestion strip are the page's three, unchanged and
 * for the page's reason: authority NOT KNOWN YET is a skeleton (silence, not a
 * default list), known-and-some renders those, known-and-none says so plainly.
 * Collapsing the first into the third would tell a person their account can
 * ask nothing on the strength of a request that had not come back.
 */
function PanelEmptyState({
  screen,
  onPick,
  disabled,
  now,
}: {
  screen: ScreenContext | undefined;
  onPick: (question: string) => void;
  disabled: boolean;
  now?: Date | undefined;
}) {
  // The SAME query keys the page uses, so the panel and the page share one
  // cache entry and one request each. A second `/auth/me` would be a second
  // answer that can disagree with the first.
  const meQuery = useQuery({
    queryKey: ['auth', 'me', 'permissions'],
    queryFn: () => auth.me(),
    staleTime: 5 * 60_000,
  });
  const toolsQuery = useQuery({
    queryKey: ['intelligence', 'tools'],
    queryFn: () => intelligence.tools(),
  });
  const mayReadPrograms = auth.holdsAtAnyScope(meQuery.data, 'program:read');
  const programsQuery = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ status: 'active', limit: 200 }),
    enabled: mayReadPrograms,
  });

  // Authority is "known" only when BOTH reads have landed: `can` default-denies
  // while `/auth/me` is in flight, and a default-denied `can` hands a
  // supervisor the self-scoped phrasing of a question they hold the whole
  // organization for.
  const authorityKnown = toolsQuery.isSuccess && meQuery.isSuccess;
  const advertisedToolIds = authorityKnown
    ? toolsQuery.data.tools.map((tool) => tool.name)
    : undefined;

  const programNames = programsQuery.isSuccess
    ? programsQuery.data.programs.map((program) => program.name).sort((a, b) => a.localeCompare(b))
    : undefined;

  const suggestions: Suggestion[] = deriveScreenSuggestions({
    advertisedToolIds,
    can: (key) => auth.can(meQuery.data, key),
    programNames,
    screen,
  });

  const first = firstNameOf(meQuery.data?.name);
  const greeting = t(greetingKeyFor((now ?? new Date()).getHours()));

  return (
    <section className="ap-empty apg-empty" aria-label="Getting started">
      <div className="ap-greeting">
        <h3 className="ap-greet">
          {first === undefined ? `${greeting}.` : `${greeting}, ${first}.`}
        </h3>
        <p className="ap-greet-ask">What do you want to know?</p>
      </div>

      {!authorityKnown && (toolsQuery.isLoading || meQuery.isLoading) ? (
        <div className="ap-suggest-skeleton">
          <SkeletonSet surface="tiles" rows={2} />
        </div>
      ) : suggestions.length > 0 ? (
        <ul className="ap-suggestions" aria-label="Suggested questions">
          {suggestions.map((suggestion) => (
            <li key={suggestion.id}>
              <button
                type="button"
                className="ap-suggestion"
                disabled={disabled}
                onClick={() => onPick(suggestion.text)}
              >
                <span className="ap-suggestion-text">{suggestion.text}</span>
                <Icon name="arrowRight" size={14} className="ap-suggestion-go" />
              </button>
            </li>
          ))}
        </ul>
      ) : authorityKnown ? (
        <p className="rn-muted ap-nosuggest">
          There are no starter questions to offer your account right now — this panel only suggests
          things your permissions can actually answer. Ask in your own words below.
        </p>
      ) : (
        <p className="rn-muted ap-nosuggest">
          We couldn’t check what your account can ask about, so there are no starters here. That is
          a failed check, not a decision about your access — ask in your own words below.
        </p>
      )}
    </section>
  );
}
